import {
  DeleteOutlined,
  Download,
  FileUploadOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import produce from "immer";
import React from "react";
import { getUniqueId } from "../../helpers";

import {
  openLoaderWithMessage,
  closeLoaderWithMessage,
  showMessage,
} from "../../redux/messages/messagesActions";
import { IFileUpload, IFileUploadProps } from "./FileUpload.type";
import {
  downLoadFile,
  getFileExtensionFromUrl,
  getFileNameFromUrl,
} from "./utils";
import { useDispatchWrapper } from "../../hooks/useDispatch";
import { ViewImageDialog } from "./Dailog/ViewImageDialog";
import { IFileExtension, getFileExtensionType } from "./utils/helper";
import { api } from "../../api/api";

export const FileUpload: React.FC<IFileUploadProps> = (props) => {
  const {
    heading = "",
    value,
    uploadType,
    placeholder = "Browse Files",
    multiple = false,
    disabled = false,
    deleteDisabled,
    onMultiChange,
    uniqueKey,
    enablePreview = false,
  } = props;

  const [localFile, setLocalFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    if (!value) {
      setLocalFile(null);
    }
  }, [value]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (props.onChange) {
        setLocalFile(file);
        setUploading(true);
        try {
          await props.onChange(file);
        } catch (error) {
          setLocalFile(null);
        } finally {
          setUploading(false);
        }
      }
    }
  };

  const handleDelete = () => {
    setLocalFile(null);
    if (props.onDelete) {
      props.onDelete();
    }
  };

  const onMultiFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && Array.isArray(value)) {
      const file = files[0];
      if (props.onMultiChange) {
        setUploading(true);
        try {
          await props.onMultiChange([
            ...value,
            {
              key: getUniqueId(),
              file: file,
              name: file.name,
              path: null,
            },
          ]);
        } finally {
          setUploading(false);
        }
      }
    }
  };

  const handleMultiChange = (data: IFileUpload[]) => {
    if (onMultiChange) {
      onMultiChange(data);
    }
  };

  if (!multiple && !Array.isArray(value)) {
    return (
      <Box width={"100%"}>
        <Typography fontWeight={600}>{heading}</Typography>

        {!localFile && !value && !uploading && (
          <Stack
            marginTop={1}
            padding={2}
            direction={"row"}
            justifyContent={"center"}
            borderRadius={1}
            border={"3px dotted gray"}
            minHeight={"50px"}
            sx={{
              opacity: disabled ? 0.4 : 1,
              cursor: disabled ? "not-allowed" : "auto",
            }}
          >
            <input
              style={{ display: "none" }}
              id={uniqueKey || "file-upload"}
              type="file"
              disabled={disabled}
              onChange={onFileChange}
            />
            <label htmlFor={uniqueKey || "file-upload"}>
              <Stack direction={"row"} justifyContent={"center"} spacing={1}>
                {uploadType === "pdf" ? (
                  <PictureAsPdfOutlined />
                ) : (
                  <FileUploadOutlined />
                )}
                <Typography variant="h5" fontWeight={700}>
                  {placeholder}
                </Typography>
              </Stack>
            </label>
          </Stack>
        )}
        {uploading && (
          <Stack
            marginTop={1}
            padding={2}
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            borderRadius={1}
            border={"3px dotted gray"}
            minHeight={"50px"}
            spacing={2}
          >
            <CircularProgress size={20} />
            <Typography variant="h5" fontWeight={700}>
              Uploading...
            </Typography>
          </Stack>
        )}
        {(localFile || value) && !uploading && (
          <Box marginTop={1}>
            <SingleFileDisplay
              file={localFile}
              value={value as string}
              onDelete={handleDelete}
              deleteDisabled={deleteDisabled}
              enablePreview={enablePreview}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box width={"100%"}>
      <Typography fontWeight={600}>{heading}</Typography>

      <Stack
        marginTop={1}
        padding={2}
        direction={"row"}
        justifyContent={"center"}
        alignItems={"center"}
        borderRadius={1}
        border={"3px dotted gray"}
        minHeight={"50px"}
        sx={{
          opacity: disabled || uploading ? 0.4 : 1,
          cursor: disabled || uploading ? "not-allowed" : "auto",
        }}
      >
        {uploading ? (
          <Stack direction={"row"} justifyContent={"center"} spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="h5" fontWeight={700}>
              Uploading...
            </Typography>
          </Stack>
        ) : (
          <>
            <input
              style={{ display: "none" }}
              id={uniqueKey || "file-upload"}
              type="file"
              disabled={disabled}
              onChange={onMultiFileChange}
            />
            <label htmlFor={uniqueKey || "file-upload"}>
              <Stack direction={"row"} justifyContent={"center"} spacing={1}>
                {uploadType === "pdf" ? (
                  <PictureAsPdfOutlined />
                ) : (
                  <FileUploadOutlined />
                )}
                <Typography variant="h5" fontWeight={700}>
                  Browse Files
                </Typography>
              </Stack>
            </label>
          </>
        )}
      </Stack>

      {Array.isArray(value) && (
        <MultiFileDisplay
          value={value}
          onChange={handleMultiChange}
          deleteDisabled={deleteDisabled}
        />
      )}
    </Box>
  );
};

export const SingleFileDisplay: React.FC<{
  file?: File | null;
  value: string | null;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  enablePreview?: boolean;
}> = (props) => {
  const { file, value, onDelete, deleteDisabled, enablePreview } = props;
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const [imageDialog, setImageDialog] = React.useState<{
    view: boolean;
    data: string;
    type: IFileExtension;
  }>({ view: false, data: "", type: "OTHER" });

  const handlePreviewFile = async () => {
    const fileType = getFileExtensionType(value || "");
    // if (fileType === "EXCEL") {
    //   setImageDialog({ view: true, data: value || "", type: fileType });
    // } else {
    //   dispatch(openLoaderWithMessage("Loading Please wait...."));
    //   downloadSingleFileAsync(value || "")
    //     .then((result) => {
    //       setImageDialog({ view: true, data: result.data, type: result.type });
    //     })
    //     .finally(() => {
    //       dispatch(closeLoaderWithMessage());
    //     });
    // }
  };

  const handleDownloadFilee = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }
    if (!loading) {
      try {
        setLoading(true);
        await downLoadFile(props.value);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadFile = async (fileName: string) => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }
    try {
      const res = await api.get("/download-docs?fileKey=" + fileName, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([res.data], {
        type: "application/" + getFileExtensionFromUrl(fileName),
      });
      // const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);

      // Create a link element and simulate a click to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileNameFromUrl(fileName)); // Set the desired file name and extension
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the URL object
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err: any) {
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "Someting went to be wrong!",
          type: "error",
        })
      );
    }
  };

  return (
    <>
      <Stack
        direction={"row"}
        border={"1px solid gray"}
        minHeight={"70px"}
        borderRadius={1}
        overflow={"hidden"}
        position={"relative"}
      >
        <Stack
          direction={"row"}
          justifyContent={"center"}
          flex={1}
          alignItems={"center"}
          sx={(theme: Theme) => ({
            background: theme.palette.grey[100],

            borderLeft: "5px solid " + theme.palette.error.main,
          })}
        >
          <InsertDriveFileOutlined fontSize="large" color="action" />
        </Stack>

        <Stack
          flex={2}
          direction={"row"}
          sx={{ background: "#fff" }}
          justifyContent={"start"}
          alignItems={"center"}
        >
          <Box pl={1}>
            <Typography
              fontWeight={600}
              variant="body1"
              sx={{ wordBreak: "break-all" }}
            >
              {file ? file.name : value ? getFileNameFromUrl(value) : ""}
            </Typography>
          </Box>
          {/* <Typography fontWeight={600}>{file ? file.siz : "Demo"}</Typography> */}
          {loading && (
            <LinearProgress
              color="error"
              sx={{ position: "absolute", bottom: 0, width: "100%" }}
            />
          )}
        </Stack>

        <Stack
          direction={"row"}
          justifyContent={"center"}
          flex={1}
          alignItems={"center"}
          spacing={1}
        >
          {enablePreview && (
            <Tooltip title="Preview File">
              <Visibility
                onClick={handlePreviewFile}
                sx={(theme) => ({
                  color: theme.palette.grey[600],
                  cursor: "pointer",
                  opacity: loading ? 0.5 : 1,
                })}
              />
            </Tooltip>
          )}
          <Tooltip title="Download">
            <Download
              onClick={() => value !== null && handleDownloadFile(value)}
              sx={(theme) => ({
                color: theme.palette.grey[600],
                cursor: "pointer",
                opacity: loading ? 0.5 : 1,
              })}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              onClick={deleteDisabled ? undefined : onDelete}
              sx={(theme) => ({
                color: theme.palette.grey[600],
                cursor: "pointer",
                opacity: deleteDisabled ? 0.5 : 1,
              })}
            />
          </Tooltip>
        </Stack>
      </Stack>
      {imageDialog.view && (
        <ViewImageDialog
          open={imageDialog.view}
          file={imageDialog.data}
          fileType={imageDialog.type}
          onClose={() =>
            setImageDialog({ view: false, data: "", type: "OTHER" })
          }
        />
      )}
    </>
  );
};

export const MultiFileDisplay: React.FC<{
  value: IFileUpload[];
  onChange: (files: IFileUpload[]) => void;
  deleteDisabled?: boolean;
}> = (props) => {
  const { value, onChange, deleteDisabled } = props;
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState<number | null>(null);

  const handleDownloadFile = (row: IFileUpload, index: number) => async () => {
    if (row.file) {
      const downloadLink = URL.createObjectURL(row.file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = row.file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }

    if (!loading && row.path) {
      try {
        setLoading(index);
        await downLoadFile(row.path);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          })
        );
      } finally {
        setLoading(null);
      }
    }
  };

  const handleDelete = (rowIndex: number) => () => {
    const newValue = produce(value, (draftValues) => {
      draftValues.splice(rowIndex, 1);
    });
    onChange(newValue);
  };

  return (
    <>
      <Stack spacing={1} marginTop={1}>
        {value.map((item, index) => {
          const fileName = item.file
            ? item.file.name
            : item.name || getFileNameFromUrl(item.path || "");
          return (
            <Stack
              direction={"row"}
              border={"1px solid gray"}
              minHeight={"70px"}
              borderRadius={1}
              overflow={"hidden"}
              position={"relative"}
            >
              <Stack
                direction={"row"}
                justifyContent={"center"}
                flex={1}
                alignItems={"center"}
                sx={(theme: Theme) => ({
                  background: theme.palette.grey[100],
                  maxWidth: "17%",
                  borderLeft: "5px solid " + theme.palette.error.main,
                })}
              >
                <InsertDriveFileOutlined fontSize="large" color="action" />
              </Stack>

              <Stack
                flex={2}
                direction={"row"}
                sx={{ background: "#fff" }}
                justifyContent={"start"}
                alignItems={"center"}
                pl={1}
              >
                <Box pl={1}>
                  <Typography
                    fontWeight={600}
                    variant="body1"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {fileName}
                  </Typography>
                </Box>
                {loading !== null && loading === index && (
                  <LinearProgress
                    color="error"
                    sx={{ position: "absolute", bottom: 0, width: "100%" }}
                  />
                )}
                {/* <Typography fontWeight={600}>{file ? file.siz : "Demo"}</Typography> */}
              </Stack>

              <Stack
                direction={"row"}
                justifyContent={"center"}
                flex={1}
                alignItems={"center"}
                spacing={1}
              >
                {/* <Tooltip title="Download">
                  <Download
                    onClick={handleDownloadFile(item, index)}
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                      cursor: "pointer",
                      opacity: loading !== null && loading === index ? 0.5 : 1,
                    })}
                  />
                </Tooltip> */}
                <Tooltip title="Delete">
                  <DeleteOutlined
                    onClick={handleDelete(index)}
                    sx={(theme) => ({
                      color: theme.palette.grey[600],
                      cursor: "pointer",
                      opacity: deleteDisabled ? 0.5 : 1,
                    })}
                  />
                </Tooltip>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};
